import Phaser from 'phaser';

const CELL_SIZE = 40;
const GRID_WIDTH = 30;
const GRID_HEIGHT = 22;

// Default colors (overridden by server-provided player colors)
const DEFAULT_COLORS = [0xff6b6b, 0x4ecdc4];

// Cell types (match server)
const EMPTY = 0;
const TERRITORY_P1 = 1;
const TERRITORY_P2 = 2;
const TRAIL_P1 = 3;
const TRAIL_P2 = 4;

interface BotState {
  x: number;
  y: number;
  color: number;
  trail: { x: number; y: number }[];
  territory: number;
  alive: boolean;
  drawMode: boolean;
}

export default class GameScene extends Phaser.Scene {
  private gridGraphics?: Phaser.GameObjects.Graphics;
  private player1Sprite?: Phaser.GameObjects.Rectangle;
  private player2Sprite?: Phaser.GameObjects.Rectangle;
  private cursors?: Phaser.Types.Input.Keyboard.CursorKeys;
  private wasd?: any;
  private spaceKey?: Phaser.Input.Keyboard.Key;
  private isDrawing = false;
  private lastDirection: string | null = null;
  private gameStarted = false;
  private attractModeActive = false;
  private playerPositions: Map<number, { x: number; y: number }> = new Map();
  private playerColors: number[] = [...DEFAULT_COLORS];
  private botGrid: number[][] = [];
  private bot1State: BotState | null = null;
  private bot2State: BotState | null = null;

  constructor() {
    super({ key: 'GameScene' });
  }

  create() {
    // Register scene globally
    window.gameScene = this;

    // Graphics for grid
    this.gridGraphics = this.add.graphics();

    // Player sprites (hidden initially)
    this.player1Sprite = this.add.rectangle(0, 0, CELL_SIZE - 4, CELL_SIZE - 4, this.playerColors[0]);
    this.player1Sprite.setStrokeStyle(2, 0xffffff);
    this.player1Sprite.setVisible(false);

    this.player2Sprite = this.add.rectangle(0, 0, CELL_SIZE - 4, CELL_SIZE - 4, this.playerColors[1]);
    this.player2Sprite.setStrokeStyle(2, 0xffffff);
    this.player2Sprite.setVisible(false);

    // Input
    this.cursors = this.input.keyboard!.createCursorKeys();
    this.wasd = this.input.keyboard!.addKeys('W,A,S,D,Q,E,Z,C');
    this.spaceKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);

    this.spaceKey.on('down', () => {
      this.isDrawing = true;
      this.sendInput();
    });
    this.spaceKey.on('up', () => {
      this.isDrawing = false;
      this.sendInput();
    });

    this.renderGrid();
    this.events.on('update', this.onUpdate, this);
  }

  /** Called at the start of every match (including rematch) to wipe visual state. */
  resetScene() {
    this.gameStarted = false;
    this.attractModeActive = false;
    this.isDrawing = false;
    this.lastDirection = null;
    this.playerPositions.clear();
    this.botGrid = [];
    this.bot1State = null;
    this.bot2State = null;

    if (this.gridGraphics) {
      this.gridGraphics.clear();
    }

    if (this.player1Sprite) {
      this.player1Sprite.setVisible(false);
      this.player1Sprite.setAlpha(1);
    }
    if (this.player2Sprite) {
      this.player2Sprite.setVisible(false);
      this.player2Sprite.setAlpha(1);
    }

    // Re-apply player colors from server state if available
    const state = window.gameClient?.['room']?.state;
    if (state) {
      if (state.player1?.color) {
        this.playerColors[0] = state.player1.color;
        this.player1Sprite?.setFillStyle(this.playerColors[0]);
      }
      if (state.player2?.color) {
        this.playerColors[1] = state.player2.color;
        this.player2Sprite?.setFillStyle(this.playerColors[1]);
      }
    }

    this.renderGrid();
  }

  startGameplay() {
    this.gameStarted = true;
    this.attractModeActive = false;

    if (this.player1Sprite) this.player1Sprite.setVisible(true);
    if (this.player2Sprite) this.player2Sprite.setVisible(true);
  }

  /** Called by main.ts when attract mode is active */
  startAttractMode() {
    this.attractModeActive = true;
    this.gameStarted = true;

    if (this.player1Sprite) this.player1Sprite.setVisible(true);
    if (this.player2Sprite) this.player2Sprite.setVisible(true);

    this.renderGrid();
  }

  /** Called by main.ts when attract mode stops */
  stopAttractMode() {
    this.attractModeActive = false;
    this.botGrid = [];
    this.bot1State = null;
    this.bot2State = null;

    if (this.player1Sprite) this.player1Sprite.setVisible(false);
    if (this.player2Sprite) this.player2Sprite.setVisible(false);

    this.renderGrid();
  }

  /** Update bot state for attract mode rendering */
  updateBotState(bot1State: BotState, bot2State: BotState) {
    if (!this.attractModeActive) return;

    this.bot1State = bot1State;
    this.bot2State = bot2State;

    // Update player sprites
    if (this.player1Sprite && bot1State) {
      this.player1Sprite.setFillStyle(bot1State.color);
      const screenX = bot1State.x * CELL_SIZE + CELL_SIZE / 2;
      const screenY = bot1State.y * CELL_SIZE + CELL_SIZE / 2;
      this.player1Sprite.setPosition(screenX, screenY);
      this.player1Sprite.setVisible(bot1State.alive);
      this.playerColors[0] = bot1State.color;
    }

    if (this.player2Sprite && bot2State) {
      this.player2Sprite.setFillStyle(bot2State.color);
      const screenX = bot2State.x * CELL_SIZE + CELL_SIZE / 2;
      const screenY = bot2State.y * CELL_SIZE + CELL_SIZE / 2;
      this.player2Sprite.setPosition(screenX, screenY);
      this.player2Sprite.setVisible(bot2State.alive);
      this.playerColors[1] = bot2State.color;
    }

    this.renderBotGrid();
  }

  /** Set the bot grid for attract mode rendering */
  setBotGrid(grid: number[][]) {
    this.botGrid = grid;
    this.renderBotGrid();
  }

  onUpdate() {
    if (!this.gameStarted || !window.gameClient) return;

    let direction: string | null = null;

    // Arrow keys
    if (this.cursors!.up.isDown && this.cursors!.left.isDown) {
      direction = 'upleft';
    } else if (this.cursors!.up.isDown && this.cursors!.right.isDown) {
      direction = 'upright';
    } else if (this.cursors!.down.isDown && this.cursors!.left.isDown) {
      direction = 'downleft';
    } else if (this.cursors!.down.isDown && this.cursors!.right.isDown) {
      direction = 'downright';
    } else if (this.cursors!.up.isDown) {
      direction = 'up';
    } else if (this.cursors!.down.isDown) {
      direction = 'down';
    } else if (this.cursors!.left.isDown) {
      direction = 'left';
    } else if (this.cursors!.right.isDown) {
      direction = 'right';
    }

    // WASD + diagonals
    if (!direction) {
      if (this.wasd!.W.isDown && this.wasd!.Q.isDown) {
        direction = 'upleft';
      } else if (this.wasd!.W.isDown && this.wasd!.E.isDown) {
        direction = 'upright';
      } else if (this.wasd!.S.isDown && this.wasd!.Z.isDown) {
        direction = 'downleft';
      } else if (this.wasd!.S.isDown && this.wasd!.C.isDown) {
        direction = 'downright';
      } else if (this.wasd!.W.isDown) {
        direction = 'up';
      } else if (this.wasd!.S.isDown) {
        direction = 'down';
      } else if (this.wasd!.A.isDown) {
        direction = 'left';
      } else if (this.wasd!.D.isDown) {
        direction = 'right';
      }
    }

    if (direction !== this.lastDirection) {
      this.lastDirection = direction;
      this.sendInput();
    }
  }

  sendInput() {
    if (window.gameClient) {
      window.gameClient.sendInput(this.isDrawing, this.lastDirection);
    }
  }

  onStateChange(state: any) {
    this.renderGrid();

    // Update player colors from state
    if (state.player1?.color) {
      this.playerColors[0] = state.player1.color;
      this.player1Sprite?.setFillStyle(this.playerColors[0]);
    }
    if (state.player2?.color) {
      this.playerColors[1] = state.player2.color;
      this.player2Sprite?.setFillStyle(this.playerColors[1]);
    }

    if (state.player1) this.updatePlayerSprite(0, state.player1);
    if (state.player2) this.updatePlayerSprite(1, state.player2);
  }

  updatePlayerSprite(playerId: number, player: any) {
    const sprite = playerId === 0 ? this.player1Sprite : this.player2Sprite;
    if (!sprite) return;

    const screenX = player.x * CELL_SIZE + CELL_SIZE / 2;
    const screenY = player.y * CELL_SIZE + CELL_SIZE / 2;

    sprite.setPosition(screenX, screenY);
    sprite.setVisible(this.gameStarted && player.alive);
    this.playerPositions.set(playerId, { x: player.x, y: player.y });
  }

  renderGrid() {
    if (!this.gridGraphics || !window.gameClient) return;

    this.gridGraphics.clear();

    const state = window.gameClient['room']?.state;
    if (!state?.grid) return;

    const grid = state.grid;

    for (let y = 0; y < GRID_HEIGHT; y++) {
      for (let x = 0; x < GRID_WIDTH; x++) {
        const cell = grid[y]?.[x];
        if (cell === undefined) continue;

        const sx = x * CELL_SIZE;
        const sy = y * CELL_SIZE;

        // Grid lines
        this.gridGraphics!.lineStyle(1, 0x1a202c, 0.3);
        this.gridGraphics!.strokeRect(sx, sy, CELL_SIZE, CELL_SIZE);

        switch (cell) {
          case 1: // TERRITORY_P1
            this.gridGraphics!.fillStyle(this.playerColors[0], 0.2);
            this.gridGraphics!.fillRect(sx + 1, sy + 1, CELL_SIZE - 2, CELL_SIZE - 2);
            break;
          case 2: // TERRITORY_P2
            this.gridGraphics!.fillStyle(this.playerColors[1], 0.2);
            this.gridGraphics!.fillRect(sx + 1, sy + 1, CELL_SIZE - 2, CELL_SIZE - 2);
            break;
          case 3: // TRAIL_P1
            this.gridGraphics!.fillStyle(this.playerColors[0], 0.8);
            this.gridGraphics!.fillRect(sx + 2, sy + 2, CELL_SIZE - 4, CELL_SIZE - 4);
            break;
          case 4: // TRAIL_P2
            this.gridGraphics!.fillStyle(this.playerColors[1], 0.8);
            this.gridGraphics!.fillRect(sx + 2, sy + 2, CELL_SIZE - 4, CELL_SIZE - 4);
            break;
        }
      }
    }
  }

  onPlayerDeath(playerId: number) {
    const sprite = playerId === 0 ? this.player1Sprite : this.player2Sprite;
    if (!sprite) return;

    const effect = this.add.graphics();
    effect.fillStyle(this.playerColors[playerId], 0.5);
    effect.fillCircle(sprite.x, sprite.y, CELL_SIZE);

    this.tweens.add({
      targets: effect,
      scale: 2,
      alpha: 0,
      duration: 500,
      onComplete: () => effect.destroy()
    });
  }

  onPlayerRespawn(playerId: number) {
    const sprite = playerId === 0 ? this.player1Sprite : this.player2Sprite;
    if (!sprite) return;

    this.tweens.add({
      targets: sprite,
      alpha: 0.3,
      duration: 100,
      yoyo: true,
      repeat: 3
    });
  }

  /** Render grid from bot state (attract mode) */
  private renderBotGrid() {
    if (!this.gridGraphics) return;

    this.gridGraphics.clear();

    // Render from bot grid
    if (!this.bot1State && !this.bot2State) return;

    const botGrid = this.botGrid;
    const p1Color = this.bot1State?.color || DEFAULT_COLORS[0];
    const p2Color = this.bot2State?.color || DEFAULT_COLORS[1];

    for (let y = 0; y < GRID_HEIGHT; y++) {
      for (let x = 0; x < GRID_WIDTH; x++) {
        const cell = botGrid[y]?.[x];
        if (cell === undefined || cell === EMPTY) continue;

        const sx = x * CELL_SIZE;
        const sy = y * CELL_SIZE;

        // Grid lines
        this.gridGraphics!.lineStyle(1, 0x1a202c, 0.3);
        this.gridGraphics!.strokeRect(sx, sy, CELL_SIZE, CELL_SIZE);

        switch (cell) {
          case TERRITORY_P1:
            this.gridGraphics!.fillStyle(p1Color, 0.2);
            this.gridGraphics!.fillRect(sx + 1, sy + 1, CELL_SIZE - 2, CELL_SIZE - 2);
            break;
          case TERRITORY_P2:
            this.gridGraphics!.fillStyle(p2Color, 0.2);
            this.gridGraphics!.fillRect(sx + 1, sy + 1, CELL_SIZE - 2, CELL_SIZE - 2);
            break;
          case TRAIL_P1:
            this.gridGraphics!.fillStyle(p1Color, 0.8);
            this.gridGraphics!.fillRect(sx + 2, sy + 2, CELL_SIZE - 4, CELL_SIZE - 4);
            break;
          case TRAIL_P2:
            this.gridGraphics!.fillStyle(p2Color, 0.8);
            this.gridGraphics!.fillRect(sx + 2, sy + 2, CELL_SIZE - 4, CELL_SIZE - 4);
            break;
        }
      }
    }

    // Also render trails directly from bot state
    if (this.bot1State) {
      for (const pos of this.bot1State.trail) {
        const sx = pos.x * CELL_SIZE + 2;
        const sy = pos.y * CELL_SIZE + 2;
        this.gridGraphics!.fillStyle(p1Color, 0.8);
        this.gridGraphics!.fillRect(sx, sy, CELL_SIZE - 4, CELL_SIZE - 4);
      }
    }

    if (this.bot2State) {
      for (const pos of this.bot2State.trail) {
        const sx = pos.x * CELL_SIZE + 2;
        const sy = pos.y * CELL_SIZE + 2;
        this.gridGraphics!.fillStyle(p2Color, 0.8);
        this.gridGraphics!.fillRect(sx, sy, CELL_SIZE - 4, CELL_SIZE - 4);
      }
    }
  }
}
